/**
 * DiscardDraftButton — descarta una versión draft v(N+1) post-canon.
 *
 * Doc 07 §"Versionado de artefactos": al confirmar, el backend marca
 * `discarded_at` en la version. El canon previo (v(N)) NO se afecta.
 *
 * OT-0009 N4 / OT-0010 (backend) — el frontend NO gatea este botón, y
 * ya no hace falta: el backend cambió la regla para que coincidiera
 * con editar. Hasta OT-0010, `discard` exigía **autor de la versión**
 * (`version.created_by_user_id`, un dato que nunca viajó en
 * `ArtifactVersionRead`) — eso era lo que impedía replicar el check
 * aquí. Ahora el endpoint aplica `load_artifact_for_edit` (dueño∪líder∪
 * admin), exactamente la misma regla que `canEditArtifact` ya calcula.
 *
 * Este botón solo se alcanza dentro de `ProjectOfficialArtifactEditView`,
 * que ya exige ese mismo `canEditArtifact` para renderizar la página
 * completa — el gate de página **es** el gate de este botón, no una
 * aproximación. Ver `ProjectOfficialArtifactEditView.test.tsx` para la
 * prueba de que el conjunto que llega hasta aquí coincide exactamente
 * con lo que el backend autoriza.
 */

import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'

import { discardDraftVersion } from '../../../api/artifacts'
import { ApiError } from '../../../api/client'
import { useAccessToken } from '../../../auth/useAccessToken'
import { Dialog } from '../../Dialog'
import { isVersionLockError, markVersionLocked } from '../../../lib/versionLock'
import { pushToast } from '../../../stores/toastStore'

interface DiscardDraftButtonProps {
  artifactId: string
  projectId: string
  /** UUID de la version draft a descartar. */
  versionId: string
  /** version_number de la draft (visible en confirm copy: "v(N+1)"). */
  versionNumber: number
  /** version_number del canon vigente (visible: "el canon v(N) sigue intacto"). */
  canonVersionNumber: number
}

export function DiscardDraftButton({
  artifactId,
  projectId,
  versionId,
  versionNumber,
  canonVersionNumber,
}: DiscardDraftButtonProps) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      await discardDraftVersion(accessToken, artifactId, versionId)
    },
    onSuccess: async () => {
      pushToast({
        kind: 'success',
        message: t('official_viewer.discard_draft.toast_success', {
          n: versionNumber,
          canon: canonVersionNumber,
        }),
      })
      setConfirmOpen(false)
      // Volver al viewer del oficial (canon vigente sigue intacto). Se sale
      // ANTES de refrescar (OT-0064): con el editor montado, la versión
      // refrescada (`is_editable=false`) pintaría la vista de bloqueo con el
      // aviso de cambios perdidos, que tras un descarte propio no es cierto.
      await navigate({
        to: '/projects/$projectId/official/$artifactId',
        params: { projectId, artifactId },
      })
      await queryClient.invalidateQueries({
        queryKey: ['artifacts', artifactId],
      })
      await queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'lifecycle'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'canon'],
      })
    },
    onError: (err: unknown) => {
      // Rechazo por estado (OT-0064): la versión entró a revisión. El editor
      // pasa al aviso de solo lectura —que desmonta este botón— y el motivo
      // viaja como toast.
      if (isVersionLockError(err)) {
        setConfirmOpen(false)
        markVersionLocked(queryClient, artifactId, versionId)
        pushToast({
          kind: 'critical',
          message: t(`errors:${err.code}` as 'errors:UNKNOWN'),
          duration: 6000,
        })
        return
      }
      const code = err instanceof ApiError ? err.code : null
      const fallback =
        err instanceof ApiError
          ? err.body?.message ?? err.message
          : err instanceof Error
            ? err.message
            : 'unknown'
      setError(
        code
          ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
          : fallback,
      )
    },
  })

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setError(null)
          setConfirmOpen(true)
        }}
        className="inline-flex items-center gap-1.5 rounded-md border border-critical/30 px-3 py-1.5 text-xs text-critical hover:bg-critical-soft transition-colors"
      >
        <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
        {t('official_viewer.discard_draft.button')}
      </button>

      <Dialog
        open={confirmOpen}
        onClose={mutation.isPending ? () => {} : () => setConfirmOpen(false)}
        title={t('official_viewer.discard_draft.confirm_title')}
      >
        <div className="space-y-4">
          <p
            className="font-body text-sm text-ink-muted"
            dangerouslySetInnerHTML={{
              __html: t('official_viewer.discard_draft.confirm_body', {
                n: versionNumber,
                canon: canonVersionNumber,
              }),
            }}
          />

          {error ? (
            <div className="rounded-md border border-critical/40 bg-critical-soft p-3 text-sm text-critical">
              {error}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={mutation.isPending}
              className="px-3 py-1.5 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
            >
              {t('official_viewer.discard_draft.cancel_button')}
            </button>
            <button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="rounded-md bg-critical px-4 py-1.5 text-sm font-medium text-canvas hover:opacity-90 disabled:opacity-50"
            >
              {mutation.isPending
                ? t('official_viewer.discard_draft.discarding')
                : t('official_viewer.discard_draft.confirm_button')}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  )
}
